import { useQueries } from '@tanstack/react-query';
import axios from '../../../sharedModules/utils/axios.utils';

export interface ManufacturerOption {
  value: string;
  label: string;
}

interface ManufacturersResponse {
  result: ManufacturerOption[];
}

const U_TRADES = ['HVAC', 'Plumbing', 'Electric', 'Water Filtration'] as const;

/** Matches {@link useGet} default key for `/home_services/manufacturers` + params. */
export function homeServiceManufacturersQueryKey(u_trade: string) {
  // Keep this key namespace unique; other modules cache AxiosResponse under the raw URL key.
  return ['home-service-manufacturers-options', { u_trade }] as const;
}

async function fetchHomeServiceManufacturers(u_trade: string): Promise<ManufacturerOption[]> {
  const res = await axios.get<ManufacturersResponse>('/home_services/manufacturers', {
    params: { u_trade },
  });
  // API shape can drift or be polluted by stale cache; only accept array payloads.
  return Array.isArray(res.data?.result) ? res.data.result : [];
}

/**
 * Loads manufacturer dropdown options per trade. Uses parallel queries with stable keys for caching.
 */
export function useHomeServiceManufacturers() {
  const queries = useQueries({
    queries: U_TRADES.map((u_trade) => ({
      queryKey: homeServiceManufacturersQueryKey(u_trade),
      queryFn: () => fetchHomeServiceManufacturers(u_trade),
    })),
  });

  const isError = queries.some((q) => q.isError);
  const isPending = queries.some((q) => q.isPending);

  return {
    hvacManufacturers: queries[0]?.data ?? [],
    plumbingManufacturers: queries[1]?.data ?? [],
    electricalManufacturers: queries[2]?.data ?? [],
    waterTreatmentManufacturers: queries[3]?.data ?? [],
    manufacturersError: isError,
    manufacturersPending: isPending,
  };
}
